import axios from 'axios';
import { supabase } from '../lib/supabase';
import { API_BASE_URL } from '../utils/apiUrl';

// Create axios instance with default config
export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add a request queue to handle requests during token refresh
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value: unknown) => void;
  reject: (error: unknown) => void;
}> = [];

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach(promise => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve(token);
    }
  });
  
  failedQueue = [];
};

// Request interceptor for authentication and debugging
api.interceptors.request.use(
  async (config) => {
    try {
      // In development, check if we should use bypass token
      if ((window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') && 
          localStorage.getItem('useDevBypass') === 'true') {
        config.headers.Authorization = 'Bearer dev-bypass-token';
        console.log('API Request:', config.method?.toUpperCase(), config.url, '[Dev Bypass]');
        return config;
      }
      
      // Get the current session with retry logic
      let retries = 0;
      let session = null;
      
      while (retries < 3) {
        try {
          const { data } = await supabase.auth.getSession();
          session = data.session;
          break;
        } catch (err) {
          retries++;
          if (retries === 3) throw err;
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }
      
      if (session?.access_token) {
        // Add authorization header
        config.headers.Authorization = `Bearer ${session.access_token}`;
        console.log('API Request:', config.method?.toUpperCase(), config.url, '[Authenticated]');
      } else {
        console.log('API Request:', config.method?.toUpperCase(), config.url, '[No Auth]');
      }
    } catch (error) {
      console.error('Error getting auth token:', error);
      // In development, use bypass token on error
      if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
        console.warn('⚠️  Auth error - using dev bypass token');
        config.headers.Authorization = 'Bearer dev-bypass-token';
        localStorage.setItem('useDevBypass', 'true');
      }
    }
    
    return config;
  },
  (error) => {
    console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for error handling and token refresh
api.interceptors.response.use(
  (response) => {
    console.log('API Response:', response.status, response.config.url);
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    
    // If we get a 401 and haven't already tried to refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      console.log('Got 401, attempting to refresh token...');
      
      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }
      
      originalRequest._retry = true;
      isRefreshing = true;
      
      try {
        // Try to refresh the session
        const { data: { session }, error: refreshError } = await supabase.auth.refreshSession();
        
        if (refreshError || !session) {
          console.error('Token refresh failed:', refreshError);
          processQueue(refreshError || new Error('No session'), null);
          
          // In development, try using bypass token before redirecting
          if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
            console.warn('⚠️  DEVELOPMENT: Using bypass token due to auth issues');
            localStorage.setItem('useDevBypass', 'true');
            originalRequest.headers.Authorization = 'Bearer dev-bypass-token';
            originalRequest._retryWithBypass = true;
            isRefreshing = false;
            return api(originalRequest);
          }
          
          // Clear auth and redirect to login
          isRefreshing = false;
          await supabase.auth.signOut();
          window.location.href = '/login';
          return Promise.reject(error);
        }
        
        // Process queued requests with new token
        processQueue(null, session.access_token);
        
        // Update the authorization header and retry
        originalRequest.headers.Authorization = `Bearer ${session.access_token}`;
        console.log('Token refreshed, retrying request...');
        
        // Dispatch event to notify components
        window.dispatchEvent(new Event('auth-refresh'));
        
        isRefreshing = false;
        return api(originalRequest);
      } catch (refreshError) {
        console.error('Token refresh error:', refreshError);
        processQueue(refreshError, null);
        isRefreshing = false;
        
        // In development, try using bypass token before redirecting
        if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
          console.warn('⚠️  DEVELOPMENT: Using bypass token due to auth error');
          localStorage.setItem('useDevBypass', 'true');
          originalRequest.headers.Authorization = 'Bearer dev-bypass-token';
          originalRequest._retryWithBypass = true;
          return api(originalRequest);
        }
        
        await supabase.auth.signOut();
        window.location.href = '/login';
        return Promise.reject(error);
      }
    }
    
    console.error('API Response Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

// Health check
export const checkHealth = async () => {
  const response = await api.get('/api/health');
  return response.data;
};